import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import { db } from "../firebase";
import "../styles/ManageProducts.css";

export default function ManageProducts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { shopId } = useParams();

  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [categoryFilter, setCategoryFilter] = useState("");

  const [stockFilter, setStockFilter] = useState("");

  // =====================================================
  // LOAD SHOP + PRODUCTS
  // =====================================================

  useEffect(() => {
    if (user && shopId) {
      loadShopAndProducts();
    }
  }, [user, shopId]);

  const loadShopAndProducts = async () => {
    try {
      setLoading(true);

      // =================================================
      // FIND SHOP
      // =================================================

      const shopRef = doc(db, "shops", shopId);

      const shopSnap = await getDoc(shopRef);

      if (!shopSnap.exists()) {
        setShop(null);
        setLoading(false);
        return;
      }

      const shopData = {
        id: shopSnap.id,
        ...shopSnap.data(),
      };

      // =================================================
      // CHECK SHOP OWNER
      // =================================================

      if (shopData.uid !== user.uid) {
        alert("You are not authorized to manage this shop.");
        navigate("/shops");
        return;
      }

      setShop(shopData);

      // =================================================
      // LOAD SHOP PRODUCTS
      // =================================================

      const productQuery = query(
        collection(db, "shopproducts"),
        where("shopId", "==", shopId)
      );

      const productSnap = await getDocs(productQuery);

      const list = productSnap.docs.map((productDoc) => ({
        id: productDoc.id,
        ...productDoc.data(),
      }));

      setProducts(list);
    } catch (err) {
      console.error("Manage products error:", err);
      alert("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
// STOCK STATUS
// =====================================================

const getStockStatus = (stock) => {
  const quantity = Number(stock || 0);

  if (quantity <= 0) {
    return {
      label: "Out of Stock",
      className: "out-of-stock",
    };
  }

  if (quantity <= 5) {
    return {
      label: "Low Stock",
      className: "low-stock",
    };
  }

  return {
    label: "In Stock",
    className: "in-stock",
  };
};

// =====================================================
// CATEGORY FILTER OPTIONS
// =====================================================

const categories = [
  ...new Set(
    products
      .map((item) => String(item.category || "").trim())
      .filter(Boolean)
  ),
].sort((a, b) => a.localeCompare(b));


// =====================================================
// SEARCH + CATEGORY + STOCK FILTER
// =====================================================

const filteredProducts = products.filter((item) => {
  const searchText = search.trim().toLowerCase();

  const productName = String(item.name || "").toLowerCase();

  const productCategory =
    String(item.category || "").trim();

  const stock = Number(item.stock || 0);

  // Search
  const matchesSearch =
    !searchText ||
    productName.includes(searchText);

  // Category
  const matchesCategory =
    !categoryFilter ||
    productCategory === categoryFilter;

  // Stock
  let matchesStock = true;

  if (stockFilter === "available") {
    matchesStock = stock > 5;
  }

  if (stockFilter === "low") {
    matchesStock = stock > 0 && stock <= 5;
  }

  if (stockFilter === "out") {
    matchesStock = stock <= 0;
  }

  return (
    matchesSearch &&
    matchesCategory &&
    matchesStock
  );
});

  // =====================================================
  // LOGIN CHECK
  // =====================================================

  if (!user) {
    return (
      <div style={{ padding: 20 }}>
        Please login.
      </div>
    );
  }

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        Loading...
      </div>
    );
  }

  // =====================================================
  // SHOP NOT FOUND
  // =====================================================

  if (!shop) {
    return (
      <div style={{ padding: 20 }}>
        You don't own a shop yet.
      </div>
    );
  }

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <div className="manage-products-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="manage-header">

        <h2>
          Manage Products
        </h2>

        <div className="manage-shop-name">
          {shop["Shop Name"]}
        </div>

        {/* =================================================
            ADD PRODUCT
        ================================================= */}

        <button
          className="add-product-btn"
          onClick={() =>
            navigate(
              `/shop/manage/${shop.id}/add-product`
            )
          }
        >
          + Add Product
        </button>

        {/* =================================================
            SEARCH
        ================================================= */}

        <div className="product-search">

          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {search && (
            <button
              type="button"
              className="product-search-clear"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}

        </div>

      </div>

{/* =================================================
    CATEGORY FILTER
================================================= */}

<div className="product-category-filter">

  <select
    value={categoryFilter}
    onChange={(e) =>
      setCategoryFilter(e.target.value)
    }
  >

    <option value="">
      All Categories
    </option>

    {categories.map((category) => (

      <option
        key={category}
        value={category}
      >
        {category}
      </option>

    ))}

  </select>

</div>

{/* =================================================
    STOCK FILTER PILLS
================================================= */}

<div className="stock-filter-pills">

  <button
    type="button"
    className={`stock-filter-pill available ${
      stockFilter === "available" ? "active" : ""
    }`}
    onClick={() =>
      setStockFilter(
        stockFilter === "available" ? "" : "available"
      )
    }
  >
    <span className="stock-pill-dot"></span>
    Available
  </button>


  <button
    type="button"
    className={`stock-filter-pill low ${
      stockFilter === "low" ? "active" : ""
    }`}
    onClick={() =>
      setStockFilter(
        stockFilter === "low" ? "" : "low"
      )
    }
  >
    <span className="stock-pill-dot"></span>
    Low Stock
  </button>


  <button
    type="button"
    className={`stock-filter-pill out ${
      stockFilter === "out" ? "active" : ""
    }`}
    onClick={() =>
      setStockFilter(
        stockFilter === "out" ? "" : "out"
      )
    }
  >
    <span className="stock-pill-dot"></span>
    Out of Stock
  </button>

</div>


      {/* =================================================
          PRODUCTS
      ================================================= */}

      {products.length === 0 ? (

        <div className="empty-products">

          <h3>
            No products added yet.
          </h3>

          <p>
            Add your first product to this shop.
          </p>

        </div>

      ) : filteredProducts.length === 0 ? (

        <div className="empty-products">

          <h3>
            No products found.
          </h3>

          <p>
            Try a different product name.
          </p>

        </div>

      ) : (

        <div className="product-list">

          {filteredProducts.map((item) => (

            <div
              key={item.id}
              className="product-card"
            >

              {/* PRODUCT IMAGE */}

              <img
                src={item.image}
                alt={item.name || "Product"}
                className="product-image"
              />

              {/* PRODUCT DETAILS */}

              <div className="product-details">

                <h3>
                  {item.name || "Unnamed Product"}
                </h3>

                <div className="product-price">
                  ₹{item.price ?? 0}
                </div>

                {/* STOCK */}

<div className="product-stock">
  Stock : {item.stock ?? 0}
</div>

{/* STOCK STATUS */}

{(() => {
  const status = getStockStatus(item.stock);

  return (
    <div
      className={`product-stock-status ${status.className}`}
    >
      {status.label}
    </div>
  );
})()}

              </div>

              {/* ACTIONS */}

              <div className="product-actions">

                <button
                  type="button"
                  className="edit-btn"
                  onClick={() =>
                    navigate(
                      `/shop/manage/${shop.id}/edit/${item.id}`
                    )
                  }
                >
                  Edit
                </button>

              </div>

            </div>

          ))}

        </div>

      )}

    </div>
  );
}